// Shared presentational primitives for the aHedge investor pages.
// Light-themed (white cards on slate canvas), no ops controls.
import type { ReactNode } from 'react';
import { cn, formatCurrency, formatPercent } from '../../lib/utils';

/** Sign-aware color for a P&L number. */
export function pnlColor(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n) || n === 0) return 'text-slate-900';
  return n > 0 ? 'text-emerald-600' : 'text-rose-600';
}

export function PageHeader({
  eyebrow,
  title,
  subtitle,
}: {
  eyebrow: string;
  title: string;
  subtitle?: string;
}) {
  return (
    <div className="mb-8">
      <p className="text-xs font-semibold uppercase tracking-wide text-blue-600">{eyebrow}</p>
      <h1 className="mt-1 text-4xl font-bold tracking-tight text-slate-900">{title}</h1>
      {subtitle && <p className="mt-2 max-w-2xl text-slate-500">{subtitle}</p>}
    </div>
  );
}

export function Card({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('rounded-xl border border-slate-200 bg-white p-6 shadow-sm', className)}>
      {children}
    </div>
  );
}

/** Headline KPI tile. `accent` darkens it (used for the primary metric). */
export function Kpi({
  label,
  value,
  sub,
  valueClass,
  accent = false,
}: {
  label: string;
  value: string;
  sub?: ReactNode;
  valueClass?: string;
  accent?: boolean;
}) {
  return (
    <div
      className={cn(
        'rounded-xl border p-5 shadow-sm',
        accent ? 'border-slate-800 bg-slate-900' : 'border-slate-200 bg-white',
      )}
    >
      <p className={cn('text-xs font-medium', accent ? 'text-slate-400' : 'text-slate-500')}>
        {label}
      </p>
      <p
        className={cn(
          'mt-2 text-2xl font-bold tabular-nums tracking-tight',
          valueClass ?? (accent ? 'text-white' : 'text-slate-900'),
        )}
      >
        {value}
      </p>
      {sub && <div className="mt-1 text-sm">{sub}</div>}
    </div>
  );
}

/** A labeled row in a breakdown list (label left, signed money right). */
export function MoneyRow({
  label,
  hint,
  value,
  bold = false,
  signed = true,
}: {
  label: string;
  hint?: string;
  value: number | null | undefined;
  bold?: boolean;
  signed?: boolean;
}) {
  return (
    <div className="flex items-center justify-between py-3">
      <div>
        <p className={cn('text-slate-700', bold && 'font-semibold text-slate-900')}>{label}</p>
        {hint && <p className="text-xs text-slate-400">{hint}</p>}
      </div>
      <p
        className={cn(
          'tabular-nums',
          bold ? 'text-lg font-bold' : 'font-medium',
          signed ? pnlColor(value) : 'text-slate-900',
        )}
      >
        {value == null
          ? '—'
          : `${signed && value > 0 ? '+' : ''}${formatCurrency(value)}`}
      </p>
    </div>
  );
}

/** Small +/- percent pill. */
export function PctPill({ pct }: { pct: number | null | undefined }) {
  if (pct == null || !Number.isFinite(pct)) return <span className="text-slate-400">—</span>;
  return (
    <span className={cn('text-sm font-medium tabular-nums', pnlColor(pct))}>
      {formatPercent(pct)}
    </span>
  );
}
